// File: webelieve-frontend/src/features/notes/components/NoteCard.tsx
// Note card component for the left panel list

import React, { useState, useRef, useEffect } from 'react';
import type { Note, NotePriority, NoteConfidentiality, NoteType } from '../types/note.types';

interface NoteCardProps {
  note: Note;
  isSelected: boolean;
  onSelect: (noteId: string) => void;
  onPin: (noteId: string, isPinned: boolean) => void;
  onDelete: (noteId: string) => void;
}

const PRIORITY_COLORS: Record<NotePriority, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const CONFIDENTIALITY_COLORS: Record<NoteConfidentiality, string> = {
  public: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  'attorney-work-product': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  'attorney-client-privilege': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const CONFIDENTIALITY_LABELS: Record<NoteConfidentiality, string> = {
  public: 'Public',
  'attorney-work-product': 'Work Product',
  'attorney-client-privilege': 'Privileged',
};

const NOTE_TYPE_ICONS: Record<NoteType, string> = {
  general: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2',
  'case-strategy': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  'client-communication': 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  'court-filing': 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
  discovery: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  settlement: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
  'deposition-summary': 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  'meeting-notes': 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  research: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  intake: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  'voice-dictation': 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
};

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const NoteCard: React.FC<NoteCardProps> = ({ note, isSelected, onSelect, onPin, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const typeIcon = NOTE_TYPE_ICONS[note.note_type] || NOTE_TYPE_ICONS.general;
  const preview = note.content_text.slice(0, 120).replace(/\n/g, ' ');

  return (
    <div
      onClick={() => onSelect(note.note_id)}
      className={`group relative px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 transition-colors ${
        isSelected
          ? 'bg-lex-accent-soft border-l-2 border-l-lex-accent'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-2 border-l-transparent'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start gap-2 mb-1">
        {/* Type icon */}
        <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={typeIcon} />
        </svg>

        {/* Title + pin */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {note.is_pinned && (
              <svg className="w-3 h-3 text-amber-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" />
                <path d="M8 9v3a1 1 0 001 1h2a1 1 0 001-1V9" />
                <path d="M10 13v4" />
              </svg>
            )}
            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {note.title}
            </h4>
          </div>
        </div>

        {/* Context menu */}
        <div className="relative shrink-0" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity"
          >
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          {showMenu && (
            <div className="absolute right-0 top-6 z-30 w-36 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPin(note.note_id, !note.is_pinned);
                  setShowMenu(false);
                }}
                className="w-full px-3 py-1.5 text-left text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                {note.is_pinned ? 'Unpin' : 'Pin to top'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(note.note_id);
                  setShowMenu(false);
                }}
                className="w-full px-3 py-1.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Preview text */}
      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2 ml-6">
        {preview || 'No content'}
      </p>

      {/* Footer row: badges + timestamp */}
      <div className="flex items-center justify-between ml-6">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Priority badge */}
          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${PRIORITY_COLORS[note.priority]}`}>
            {note.priority}
          </span>

          {/* Confidentiality badge */}
          {note.confidentiality !== 'public' && (
            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${CONFIDENTIALITY_COLORS[note.confidentiality]}`}>
              {CONFIDENTIALITY_LABELS[note.confidentiality]}
            </span>
          )}

          {/* Tags (first 2) */}
          {note.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="inline-flex px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
            >
              {tag}
            </span>
          ))}
          {note.tags.length > 2 && (
            <span className="text-[10px] text-gray-400">+{note.tags.length - 2}</span>
          )}
        </div>

        {/* Timestamp */}
        <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
          {formatRelativeDate(note.updated_at)}
        </span>
      </div>

      {/* Matter/client context */}
      {(note.matter_name || note.client_name) && (
        <div className="flex items-center gap-1.5 mt-1.5 ml-6">
          {note.matter_name && (
            <span className="text-[10px] text-lex-accent truncate max-w-[120px]">
              {note.matter_name}
            </span>
          )}
          {note.matter_name && note.client_name && (
            <span className="text-[10px] text-gray-300 dark:text-gray-600">/</span>
          )}
          {note.client_name && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
              {note.client_name}
            </span>
          )}
        </div>
      )}

      {/* Collaboration + Intelligence indicators */}
      <div className="flex items-center gap-2 mt-1 ml-6">
        {/* Collaborator count */}
        {(note.collaborator_count ?? 0) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-lex-accent">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {note.collaborator_count}
          </span>
        )}

        {/* Comment count */}
        {(note.comment_count ?? 0) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {note.comment_count}
          </span>
        )}

        {/* Intelligence: tasks */}
        {note.intelligence && note.intelligence.extracted_tasks_count > 0 && (
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
            {note.intelligence.extracted_tasks_count} task{note.intelligence.extracted_tasks_count > 1 ? 's' : ''}
          </span>
        )}

        {/* Intelligence: deadlines */}
        {note.intelligence && note.intelligence.extracted_deadlines_count > 0 && (
          <span className="text-[10px] text-amber-600 dark:text-amber-400">
            {note.intelligence.extracted_deadlines_count} deadline{note.intelligence.extracted_deadlines_count > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default NoteCard;
